import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import "./FloatingButtons.css";

import {useUser} from "../context/UserContext.js";
import {toast} from "react-hot-toast";
import formatDate from "../_utils/formatDate.js";

import * as queryService from "../_services/query.service.js";

const FloatingButtons = () => {
  const [loading, setLoading] = useState(false);
  const {user} = useUser();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef(null);
  const [queries, setQueries] = useState([]);

  useEffect(() => {
    if(!user || !isOpen) return;
    
    const handleFetch = async () => {
      setLoading(false);

      try {
        const getQueries = await queryService.fetchQueries();  
        setQueries(getQueries.data);
      } catch (error) {
        toast.error(error.message || "Unable to fetch queries");
        setQueries([]);
      } finally {
        setLoading(false);
      }
    }

    handleFetch();
  }, [user, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if(
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);

  const handleFabClick = () => {
    if(!user) {
      navigate("/contact");
      return;
    }

    setIsOpen((prev) => !prev);
  };

  const handleQueryClick = (query) => {
    setIsOpen(false);

    navigate(
      `/chats/?queryId=${query.queryId}`
    );
  };

  const handleSeeAll = () => {
    setIsOpen(false);
    navigate("/chats");
  };

  return (
    <div
      className="fab-container"
      ref={popupRef}
    >
      {user && (
        <div
          className={`chat-popup ${
            isOpen ? "chat-popup-open" : ""
          }`}
        >
          <div className="chat-popup-header">
            <div>
              <h6>Support Chats</h6>
              <span>Your recent queries</span>
            </div>

            <button
              type="button"
              className="chat-popup-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chats"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          <div className="chat-query-list">
            {!loading && queries.length > 0 ? (
              queries.map((query) => (
                <button
                  key={query.queryId}
                  type="button"
                  className="chat-query-item"
                  onClick={() => handleQueryClick(query)}
                >
                  <div className="chat-query-icon">
                    <i className="bi bi-chat-dots" />
                  </div>

                  <div className="chat-query-content">
                    <div className="chat-query-top">
                      <strong>
                        {query.subject}
                      </strong>

                      <span>
                        {formatDate(query.updatedAt, "relative")}
                      </span>
                    </div>

                    <p>
                      {query.chats[Number(query.chats.length > 0 ? query.chats.length -1 : 0)]?.message || "No chats yet"}
                    </p>

                    <div className="chat-query-meta">
                      <span className="ticket-number">
                        Ticket: {query.ticketNumber}
                      </span>

                      <span
                        className={`query-status ${query.status.toLowerCase()}`}
                      >
                        {query.status.replace("_", " ")}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            ) : !loading && !queries.length
            ?  (
              <div className="chat-empty">
                <i className="bi bi-chat-square-text" />

                <p>
                  You don't have any support queries yet.
                </p>
              </div>
            ) : (
              <div className="chat-empty">
                <i className="bi bi-chat-square-text" />

                <p>
                  Loading Recent Queries
                </p>
              </div>
            )}
          </div>

          <div className="chat-popup-footer">
            <button
              type="button"
              className="see-all-button"
              onClick={handleSeeAll}
            >
              <span>See All Chats</span>

              <i className="bi bi-arrow-right" />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`fab ${isOpen ? "fab-active" : ""}`}
        onClick={handleFabClick}
        aria-label="Support chats"
      >
        <i
          className={`bi ${
            isOpen
              ? "bi-x-lg"
              : "bi-chat-dots"
          } fab-icon`}
        />
      </button>
    </div>
  );
};

export default FloatingButtons;